import React, { useState } from 'react';
import { Link } from 'gatsby';
import { AppBar, Drawer, List, ListItem, Stack, Toolbar, Typography, styled } from '@mui/material';

import { navigationLinks } from 'utils/data';
import { Button, Icon, IconButton } from 'ui';
import { NavLinkAnimator } from 'components/animations';
import logoImg from 'images/logo.png';

const NavLink = ({ href, children, serialNum, ...rest }) => (
  <NavLinkAnimator delay={serialNum / 10}>
    <NavItem to={href} activeClassName='nav-link-active' {...rest}>
      <Typography variant='body2' color='slate.lighter'>
        <NavLinkNumber>0{serialNum}.</NavLinkNumber>
        {children}
      </Typography>
    </NavItem>
  </NavLinkAnimator>
);

const Navigation = () => {
  const [openMobileNav, setOpenMobileNav] = useState();
  const toggleMobileNav = visibility => () => setOpenMobileNav(visibility);

  return (
    <>
      <NavBar position='fixed'>
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <LogoLinkWrapper to='/'>
            <Logo alt='LOGO' src={logoImg} />
          </LogoLinkWrapper>

          <NavToolBar component={Stack} direction='row' gap={4} textTransform='capitalize'>
            {navigationLinks.map((navLink, idx) => (
              <NavLink key={idx} serialNum={idx} href={navLink.href}>
                {navLink.text}
              </NavLink>
            ))}

            <NavLinkAnimator delay={navigationLinks.length / 10}>
              <Button component='a' target='_blank' href='/resume.pdf'>
                resume
              </Button>
            </NavLinkAnimator>
          </NavToolBar>

          <Hamburger onClick={toggleMobileNav(true)}>
            <Icon name='Menu' color='secondary.main' />
          </Hamburger>
        </Stack>
      </NavBar>

      <Drawer
        sx={{ zIndex: 999999 }}
        open={openMobileNav}
        anchor='top'
        onClose={toggleMobileNav(false)}>
        <MobileNav>
          <Stack alignItems='flex-end' px={2}>
            <IconButton onClick={toggleMobileNav(false)}>
              <Icon name='X' color='secondary.main' />
            </IconButton>
          </Stack>

          <List>
            {navigationLinks.map((navLink, idx) => (
              <MobileNavItem key={idx}>
                <NavLink serialNum={idx} href={navLink.href} onClick={toggleMobileNav(false)}>
                  {navLink.text}
                </NavLink>
              </MobileNavItem>
            ))}
          </List>

          <NavLinkAnimator style={{ alignSelf: 'center' }} delay={navigationLinks.length / 10}>
            <Button component='a' target='_blank' href='/resume.pdf'>
              resume
            </Button>
          </NavLinkAnimator>
        </MobileNav>
      </Drawer>
    </>
  );
};

export default Navigation;

const NavBar = styled(AppBar)(({ theme }) => ({
  backdropFilter: 'blur(16px) saturate(180%)',
  boxShadow: '0 1.5rem 3rem -.75rem rgba(0, 0, 0, .25)',
  backgroundColor: 'rgba(10, 25, 47, .75)',
  zIndex: theme.zIndex.modal,
  padding: theme.spacing(1.5, 3),
}));

const NavToolBar = styled(Toolbar)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const LogoLinkWrapper = styled(Link)({
  display: 'flex',
  alignItems: 'center',
});

const Logo = styled('img')({
  height: '4rem',
});

const NavLinkNumber = styled('span')(({ theme }) => ({
  color: theme.palette.secondary.main,
  marginRight: '.5rem',
}));

const NavItem = styled(Link)(({ theme }) => ({
  position: 'relative',

  '&::before': {
    content: '""',
    position: 'absolute',
    width: '100%',
    height: '2px',
    backgroundColor: theme.palette.secondary.main,
    bottom: '-.6rem',
    transform: 'scaleX(0)',
    transition: 'all .4s',
    transformOrigin: 'left',
  },

  '&:hover::before': {
    transform: 'scaleX(1)',
  },
}));

const Hamburger = styled(IconButton)(({ theme }) => ({
  display: 'none',

  [theme.breakpoints.down('md')]: {
    display: 'block',
  },
}));

const MobileNav = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2, 0),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.slate.lighter,
  gap: theme.spacing(2),
  overflowY: 'hidden',
}));

const MobileNavItem = styled(ListItem)({
  display: 'flex',
  justifyContent: 'center',
  textTransform: 'capitalize',
});
